import './index.less';
import Barcode from 'react-jsbarcode';
import { useState, useEffect } from 'react';
import classNames from 'classnames';
import { getQueryString } from '../shared/utils/index';
import { getEnvUrlPrefix } from '../shared/utils/multiRegionUtil';
import { ReactComponent as ShopeeExpressLogo } from './assets/shopeeExpressLogo.svg';
import { ReactComponent as ServiceError } from './assets/serviceError.svg';
import Loading from '../shared/components/loading';
import { getSMSContent, getInOrderContent, getPermission } from './api';
import { $gt } from '../lang/lang';
import { IS_MY } from '../shared/utils/multiRegion';
import Storage from '../shared/utils/localStorage';
import { OrderReqParams, ReqParams } from './types';

const safeGetStorage = (local: string|null) => local && Storage.getCanExpireLocalStorage(local);
const getShipmentIdFromQuery = safeGetStorage(getQueryString('shipment_id'));
const fifteenMin = 15 * 60 * 1000;

function App() {
  const [from, setFrom] = useState(getQueryString('from'));
  const [fromSMS, setFromSMS] = useState(getQueryString('from') === 'sms');
  const [fromApp, setFromApp] = useState(getQueryString('from') === 'ODP');
  const [signature, setSignature] = useState(getQueryString('signature'));
  const [encode_shipment_id, setEncodeShipmentId] = useState(
    getQueryString('shipment_id'),
  );
  const [loading, setLoading] = useState(true);
  const [contentType, setContentType] = useState('error');
  const [showRefreshButton, setShowRefreshButton] = useState(safeGetStorage('showRefreshButton') || false);
  const storageInfo = getShipmentIdFromQuery;
  const [errorMessage, setErrorMessage] = useState<string>();
  const [info, setInfo] = useState(getShipmentIdFromQuery);

  const checkVerifyPermission = async () => {
    const { data: resData } = await getPermission();
    const { data: { collection } } = resData;
    return !!collection.enable_captcha_verification;
  };

  const navigateToCaptcha = () => {
    window.location.replace(
      `https://${getEnvUrlPrefix()}shopee.com.my/verify/captcha?client_id=2&next=${encodeURIComponent(
        `https://spx.${getEnvUrlPrefix()}shopee.com.my/m/barcode/index.html?from=sms&shipment_id=${encode_shipment_id}`,
      )}&scene=enter_url&app_key=SPXBuyer.RW`,
    );
  };
  const showShopeeOrderContent = async (params: OrderReqParams) => {
    setLoading(true);
    try {
      const { data: resData } = await getInOrderContent(params);
      const { retcode, data, message } = resData;
      if (retcode === 0) {
        setInfo(data);
        setContentType('normal');
      } else {
        setErrorMessage(message);
      }
    } catch ({ message }) {
      setErrorMessage(message as any);
      setContentType('error');
      console.error('Get self collection content error:', message);
    } finally {
      setLoading(false);
    }
  };

  const showSMSContent = async (params: {
    shipment_id: string;
    signature?: string;
  }, verify:boolean) => {
    setLoading(true);
    if (!signature && verify) {
      navigateToCaptcha();
      return;
    }
    try {
      if (!encode_shipment_id) return;
      const { data: resData } = await getSMSContent(params);
      const { retcode, data, message } = resData;
      if (retcode === 0) {
        setInfo(data);
        setContentType('normal');
        Storage.setCanExpireLocalStorage(encode_shipment_id, data, fifteenMin);
      } else {
        if (retcode === 1072001) { // signature
          setErrorMessage(message);
          if (verify) {
            navigateToCaptcha();
          }
          return;
        }
        Storage.setCanExpireLocalStorage(encode_shipment_id, message, fifteenMin);
        if (retcode === 500) {
          setShowRefreshButton(true);
          Storage.setCanExpireLocalStorage('showRefreshButton', true, fifteenMin);
        }
        setErrorMessage(message);
      }
    } catch ({ message }) {
      setErrorMessage(message as any);
      console.error('Get self collection content error:', message);
    } finally {
      setLoading(false);
    }
  };

  const setError = (message: string) => {
    setErrorMessage(message);
    setLoading(false);
    throw new Error(message);
  };

  const isStorageExist = (params: any) => {
    if (!params) return false; // in case null === object
    if (typeof params === 'object') {
      return Object.keys(params).length !== 0;
    } return !!params;
  };

  const init = async () => {
    try {
      if (!IS_MY) {
        setError($gt('Your current IP is not in MY. In order to protect personal data, this request would be rejected!'));
      }
      if (!encode_shipment_id) {
        setError($gt('No Order'));
      }
      if (!from) {
        setError($gt('No Referer'));
      }
      if (isStorageExist(storageInfo)) {
        const type = (typeof storageInfo === 'string' ? 'error' : 'normal');
        setContentType(type);
        setLoading(false);
        return;
      }

      if (fromSMS) {
        const params:ReqParams = {
          shipment_id: encode_shipment_id || '',
        };
        const enableVerify = await checkVerifyPermission();
        if (enableVerify) {
          params.signature = signature || '';
        }
        await showSMSContent(params, enableVerify);
      } else if (fromApp) {
        const params: OrderReqParams = {
          shipment_id: getQueryString('shipment_id') || '',
          token: getQueryString('token') || '',
        };
        await showShopeeOrderContent(params);
      }
    } catch (e) {
      console.error('init failed: ', e);
    }
  };

  const reloadPage = () => {
    window.location.reload();
  };

  const smsHead = () => (
    <div className="header">
      <div className="logo">
        <ShopeeExpressLogo />
      </div>
      {contentType !== 'error' && (
        <div className="tips">{$gt('Collect parcel using the following details')}</div>
      )}
    </div>
  );

  const smsInfo = () =>
    info?.service_point_name && (
      <div className="info">
        <div className="sms-info-header">
          <span>{$gt('Service Point')}</span>
        </div>
        <div className="sms-info-content">{info?.service_point_name}</div>
      </div>
    );

  const shopeeOrderInfo = () =>
    info?.user_name && (
      <div className="info">
        <div>
          <span className="info-label">{$gt('Name: ')}</span>
          <span className="info-value"> {info?.user_name}</span>
        </div>
        <div>
          <span className="info-label">{$gt('Phone: ')}</span>
          <span className="info-value"> {info?.user_phone}</span>
        </div>
      </div>
    );

  const errorView = () => (
    <div className="wrapper error-wrapper">
      <div className="error-content">
        <div className="service-error-logo">
          <ServiceError />
        </div>
        <div className="error-message">{errorMessage || 'error'}</div>
        {showRefreshButton && (
          <div className="error-button" onClick={reloadPage}>
            {$gt('Refresh')}
          </div>
        )}
      </div>
    </div>
  );

  const normalView = () => (
    <div className={classNames('wrapper', { 'sms-wrapper': fromSMS })}>
      {fromSMS && contentType !== 'error' ? smsInfo() : shopeeOrderInfo()}
      <div className="barcode-view">
        <div className="barcode-wrapper">
          {info?.shipment_id && (
            <Barcode
              value={info?.shipment_id}
              className="barcode-content"
              options={{
                displayValue: false,
              }}
            />
          )}
        </div>
        {info?.shipment_id && <div className="barcode-text">{info?.shipment_id}</div>}
      </div>
      <div className="cut-line cut-round" />
      <div className="barcode-view">
        <div className="barcode-wrapper">
          {info?.pin && (
            <Barcode
              value={info?.pin}
              className="barcode-content"
              options={{
                width: 5,
                displayValue: false,
              }}
            />
          )}
        </div>
        <div className="barcode-text">
          {info?.pin && (
            <span>
              {$gt('PIN: ')}<span>{info?.pin}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const showContentView = () => (contentType === 'error' ? errorView() : normalView());

  useEffect(() => {
    init();
  }, []);

  return (
    <div
      className={classNames('page', { 'landing-container-loading': loading })}
    >
      {fromSMS && smsHead()}
      {loading ? <Loading /> : showContentView()}
    </div>
  );
}

export default App;
